import React, { useEffect, useState } from 'react';
import { db } from '../firebaseConfig'; 
import { collection, onSnapshot, doc, updateDoc } from "firebase/firestore";
import { Zap, Crosshair, Server, CheckCircle2, XCircle, Plus, ShieldCheck } from 'lucide-react';

const AdminSpyRadar = () => {
  const [allClients, setAllClients] = useState([]);
  const [isScanning, setIsScanning] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState('client_1030776826781583'); 
  const [compName, setCompName] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "clients"), (snapshot) => {
      setAllClients(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, []);

  // 🔥 PRO FIX: Perfect Fetch URL & Error Handling
  const triggerMasterScan = async (clientId, competitorId) => {
    setIsScanning(competitorId); 
    try {
        const url = `https://us-central1-whatsapp-autoreply-bot-pivot.cloudfunctions.net/testSpyEngine?clientId=${clientId}&competitorId=${competitorId}`;
        const response = await fetch(url);
        
        if (!response.ok) {
            throw new Error(`Server gave status: ${response.status}`);
        }
        console.log(`✅ Scan sent for Competitor: ${competitorId}`);
    } catch (e) {
        console.error("❌ Scan Failed:", e);
        alert("Scan shuru nahi ho paya. Backend check karein.");
    } finally {
        setIsScanning(null); // Button ko wapas zinda kar do chahe success ho ya fail
    }
  };

  const handleAddCompetitor = async (e) => {
    e.preventDefault();
    if (!selectedClientId || !compName.trim()) return alert("Dono detail bharein!");
    setIsAdding(true);
    try {
      const clientRef = doc(db, "clients", selectedClientId);
      const targetClient = allClients.find(c => c.id === selectedClientId);
      const existing = targetClient?.competitors || [];
      if (existing.length >= 3) return alert("Max 3 targets allowed.");

      await updateDoc(clientRef, {
        competitors: [...existing, { id: Date.now().toString(), name: compName.trim(), addedAt: Date.now(), status: "PENDING" }]
      });
      setCompName('');
    } catch (error) { alert(error.message); }
    setIsAdding(false);
  };

  const handleRemoveCompetitor = async (clientId, competitorId) => {
    if (!window.confirm("Is target ko delete karein?")) return;
    const clientRef = doc(db, "clients", clientId);
    const target = allClients.find(c => c.id === clientId);
    await updateDoc(clientRef, { competitors: target.competitors.filter(c => c.id !== competitorId) });
  };

  const clientsWithCompetitors = allClients.filter(c => c.competitors?.length > 0);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 max-w-5xl mx-auto space-y-8">
      <div className="flex items-center gap-3 border-b pb-4">
        <Server className="w-6 h-6 text-indigo-600"/>
        <h2 className="text-xl font-black text-slate-900 uppercase">God Mode: Spy Radar</h2>
      </div>

      <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
        <form onSubmit={handleAddCompetitor} className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
                <label className="text-[10px] font-bold text-slate-500 uppercase">Workspace</label>
                <select value={selectedClientId} onChange={(e) => setSelectedClientId(e.target.value)} className="w-full p-2.5 rounded-lg border text-xs font-bold bg-white">
                    <option value="">-- Choose Client --</option>
                    {allClients.map(c => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
                </select>
            </div>
            <div className="flex-[2]">
                <label className="text-[10px] font-bold text-slate-500 uppercase">Competitor Name + City</label>
                <input type="text" value={compName} onChange={(e) => setCompName(e.target.value)} placeholder="e.g. TGC Dehradun" className="w-full p-2.5 rounded-lg border text-xs bg-white" required />
            </div>
            <button type="submit" disabled={isAdding} className="bg-slate-900 text-white px-6 py-2.5 rounded-lg text-[10px] font-black uppercase flex items-center gap-2 hover:bg-indigo-600 disabled:opacity-50">
                {isAdding ? 'Deploying...' : <><Plus size={14}/> Lock Target</>}
            </button>
        </form>
      </div>

      <div className="overflow-x-auto border rounded-xl">
        <table className="w-full text-left">
          <thead className="bg-slate-50 border-b text-[10px] font-black text-slate-500 uppercase">
            <tr>
              <th className="px-4 py-3">Workspace</th>
              <th className="px-4 py-3">Target</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Control</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {clientsWithCompetitors.map(client => client.competitors.map(comp => (
              <tr key={comp.id} className="hover:bg-slate-50/80 group">
                <td className="px-4 py-4 text-sm font-bold">{client.name}</td>
                <td className="px-4 py-4 text-xs font-bold text-slate-700 flex items-center gap-2"><Crosshair size={14} className="text-rose-500"/>{comp.name}</td>
                <td className="px-4 py-4">
                    <span className={`text-[10px] font-bold px-2 py-1 rounded border ${comp.status === 'FOUND' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-amber-50 text-amber-500 border-amber-100'}`}>
                        {comp.status === 'FOUND' ? `Found: ${comp.rating} ⭐` : comp.status}
                    </span>
                </td>
               <td className="px-4 py-4 text-right flex justify-end gap-2">
    {/* ✨ FIXED: Remove opacity-0, change icon color to always visible red */}
    <button 
        onClick={() => handleRemoveCompetitor(client.id, comp.id)} 
        className="p-2 bg-rose-50 text-rose-500 border border-rose-100 hover:bg-rose-500 hover:text-white rounded-lg transition-all shadow-sm flex items-center justify-center"
        title="Delete Target"
    >
        <XCircle size={16}/>
    </button>
    {/* 🔥 PRO FIX: Perfect disabled state and visual feedback */}
                    <button 
                        onClick={() => triggerMasterScan(client.id, comp.id)} 
                        disabled={isScanning === comp.id || comp.status === 'SCANNING'} 
                        className={`text-[10px] font-black px-4 py-2 rounded-lg uppercase flex items-center gap-1.5 ${isScanning === comp.id || comp.status === 'SCANNING' ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-indigo-600 text-white hover:shadow-lg hover:bg-indigo-700'}`}
                    >
                        {isScanning === comp.id || comp.status === 'SCANNING' ? (
                            <><div className="w-3 h-3 border-2 border-slate-300 border-t-slate-500 rounded-full animate-spin"></div> Scanning...</>
                        ) : (
                            <><Zap size={14}/> Force Scan</>
                        )}
                    </button>
                </td>
              </tr>
            )))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminSpyRadar;